import React from 'react';
import { LegalLayout } from '@/components/layouts/LegalLayout';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';

export const ConditionsUtilisation: React.FC = () => {
  return (
    <LegalLayout>
      <div className="prose prose-slate dark:prose-invert max-w-none">
        <h1 className="text-3xl font-bold mb-6">Conditions Générales d'Utilisation</h1>
        
        <p className="text-muted-foreground mb-8">
          Dernière mise à jour : [Date - À remplir]
        </p>

        <Alert className="mb-8">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            En utilisant notre service, vous acceptez ces conditions d'utilisation dans leur intégralité.
          </AlertDescription>
        </Alert>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">1. Description du service</h2>
          <div className="bg-muted/30 p-4 rounded-lg">
            <p>
              Notre plateforme SaaS propose une solution complète de gestion de stock et de caisse destinée 
              aux commerces et entreprises. Le service inclut :
            </p>
            <ul className="list-disc list-inside mt-3 space-y-1">
              <li>Gestion des stocks et inventaire</li>
              <li>Système de caisse et point de vente</li>
              <li>Suivi des ventes et rapports</li>
              <li>Gestion multi-utilisateurs</li>
              <li>Sauvegarde sécurisée des données</li>
            </ul>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">2. Accès au service</h2>
          
          <div className="space-y-4">
            <div className="border border-border rounded-lg p-4">
              <h3 className="font-semibold mb-2">📝 Création de compte</h3>
              <ul className="text-sm space-y-1">
                <li>• Vous devez fournir des informations exactes et à jour</li>
                <li>• Un seul compte par entreprise/utilisateur principal</li>
                <li>• Vérification de l'adresse email obligatoire</li>
              </ul>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h3 className="font-semibold mb-2">🔐 Sécurité du compte</h3>
              <ul className="text-sm space-y-1">
                <li>• Vous êtes responsable de la confidentialité de vos identifiants</li>
                <li>• Changez régulièrement votre mot de passe</li>
                <li>• Signalez immédiatement tout accès non autorisé</li>
              </ul>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">3. Obligations de l'utilisateur</h2>
          
          <div className="bg-destructive/10 border border-destructive/20 p-4 rounded-lg mb-4">
            <h3 className="font-semibold mb-2 text-destructive">🚫 Usages interdits</h3>
            <ul className="text-sm space-y-1">
              <li>• Utilisation contraire aux lois en vigueur</li>
              <li>• Tentative de piratage ou d'accès non autorisé</li>
              <li>• Partage de contenus illégaux ou diffamatoires</li>
              <li>• Utilisation de robots, scripts automatisés non autorisés</li>
              <li>• Revente ou sous-location du service sans autorisation</li>
            </ul>
          </div>

          <div className="bg-primary/10 border border-primary/20 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">✅ Bonnes pratiques</h3>
            <ul className="text-sm space-y-1">
              <li>• Sauvegardez régulièrement vos données importantes</li>
              <li>• Respectez les limites d'utilisation de votre abonnement</li>
              <li>• Maintenez vos informations de compte à jour</li>
              <li>• Signalez les bugs ou problèmes de sécurité</li>
            </ul>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">4. Suspension et résiliation</h2>
          
          <div className="grid gap-4">
            <div className="border border-orange-200 bg-orange-50 dark:bg-orange-950/20 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">⚠️ Suspension temporaire</h3>
              <p className="text-sm">
                Nous pouvons suspendre votre accès en cas de :
              </p>
              <ul className="text-sm mt-2 space-y-1">
                <li>• Non-paiement des frais d'abonnement</li>
                <li>• Violation des conditions d'utilisation</li>
                <li>• Activité suspecte ou risque de sécurité</li>
              </ul>
            </div>

            <div className="border border-red-200 bg-red-50 dark:bg-red-950/20 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">🔒 Résiliation définitive</h3>
              <p className="text-sm">
                Résiliation possible en cas de violations graves ou répétées. 
                Vos données seront conservées 30 jours pour récupération puis supprimées.
              </p>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">5. Limitation de responsabilité</h2>
          
          <div className="bg-muted/30 p-4 rounded-lg space-y-3">
            <p className="text-sm">
              <strong>Disponibilité du service :</strong> Nous nous efforçons de maintenir un service 
              disponible 24h/24, mais ne garantissons pas une disponibilité de 100%.
            </p>
            
            <p className="text-sm">
              <strong>Perte de données :</strong> Bien que nous sauvegardions régulièrement vos données, 
              nous vous recommandons d'effectuer vos propres sauvegardes.
            </p>
            
            <p className="text-sm">
              <strong>Dommages indirects :</strong> Notre responsabilité est limitée au montant de 
              votre abonnement mensuel en cours.
            </p>
          </div>
        </section>

        <Separator className="my-6" />

        <section>
          <h2 className="text-xl font-semibold mb-4">6. Modifications des CGU</h2>
          <p>
            Nous nous réservons le droit de modifier ces conditions à tout moment. 
            Les utilisateurs seront notifiés par email des changements importants 30 jours avant leur entrée en vigueur.
          </p>
          
          <div className="bg-primary/10 p-4 rounded-lg mt-4">
            <p className="text-sm">
              <strong>Contact :</strong> [support@votredomaine.com - À remplir]<br/>
              <strong>Droit applicable :</strong> Droit français<br/>
              <strong>Juridiction :</strong> [Tribunal de votre siège social - À remplir]
            </p>
          </div>
        </section>
      </div>
    </LegalLayout>
  );
};